import type { Theme, ThemeContent } from "@/types/theme.types";

/**
 * Pure state derivation for the chat theme card (#54).
 *
 * The card's "applied" state is SERVER-derived: the backend stores the
 * `sourceKey` (the card's toolUseId) of whichever card performed the current
 * apply inside `metadata.latestAppliedTheme`. No localStorage, no content
 * hashing — a re-issued identical theme (new toolUseId) is a fresh card that
 * applies again by construction.
 */

export type ThemeCardState = "auto-apply" | "applied" | "stale" | "manual";

export interface AppliedThemeInfo {
  appliedAt: string;
  sourceKey?: string;
  themeId?: string;
}

export interface DeriveCardStateInput {
  /** The sidecar flagged this card for auto-apply (generate_theme_hitl). */
  autoApply: boolean;
  /**
   * The card arrived live in this tab (its first render carried a
   * `hitl-pending-*` message id). Historical cards restored from session
   * history must NEVER auto-fire — that is root causes A1/A2 of #54.
   */
  wasLiveOnMount: boolean;
  /** The chat message's own timestamp (ISO). Undefined when unknown. */
  cardTimestamp?: string;
  /** This card's identity (toolUseId, fallback messageId). */
  applySourceKey?: string;
  /** Server truth from getProjectTheme(); null = no theme applied. */
  applied: AppliedThemeInfo | null;
}

export function deriveCardState(input: DeriveCardStateInput): ThemeCardState {
  const { autoApply, wasLiveOnMount, cardTimestamp, applySourceKey, applied } =
    input;

  // This exact card performed the currently-applied theme.
  if (
    applied?.sourceKey &&
    applySourceKey &&
    applied.sourceKey === applySourceKey
  ) {
    return "applied";
  }

  // A newer apply postdates this card → its suggestion is superseded. Both
  // server timestamps (appliedAt = apply time, cardTimestamp = entry
  // createdAt), so no client-clock skew.
  const superseded =
    applied !== null &&
    cardTimestamp !== undefined &&
    Date.parse(applied.appliedAt) >= Date.parse(cardTimestamp);

  // Live cards auto-apply once; the superseded check is the backstop for a
  // refresh-mid-turn where the recovered card's apply already landed.
  if (autoApply && wasLiveOnMount && !superseded) {
    return "auto-apply";
  }

  if (superseded) {
    return "stale";
  }

  return "manual";
}

/**
 * Parse a theme `content` JSON string. Tolerates double-encoded content (a
 * stale API pod during the #54 rollout double-stringified the TEXT column).
 * Returns null on anything unparseable.
 */
export function parseThemeContentSafe(content: string): ThemeContent | null {
  try {
    const parsed: unknown = JSON.parse(content);
    if (typeof parsed === "string") {
      return JSON.parse(parsed) as ThemeContent;
    }
    return parsed as ThemeContent;
  } catch {
    return null;
  }
}

/**
 * Stable stringify: recursively sorts object keys so semantically-equal
 * cssVars objects serialize identically regardless of key order.
 */
function stableStringify(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(",")}]`;
  }
  if (value !== null && typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => `${JSON.stringify(k)}:${stableStringify(v)}`);
    return `{${entries.join(",")}}`;
  }
  return JSON.stringify(value);
}

function normalizeIdentity(name: string, cssVars: unknown): string {
  return `${name.trim().toLowerCase()}|${stableStringify(cssVars)}`;
}

/**
 * Find an existing library theme whose content matches the card's theme
 * (normalized name + deep-equal cssVars), so re-applying a previously-saved
 * suggestion reuses its `themes` row instead of minting a duplicate
 * `custom-<nanoid>` every time (#54 library pollution).
 */
export function findReusableTheme(
  library: Theme[],
  content: Pick<ThemeContent, "name" | "cssVars">,
): Theme | null {
  const target = normalizeIdentity(content.name, content.cssVars);

  for (const candidate of library) {
    if (candidate.type !== "custom") continue;
    try {
      const parsed = JSON.parse(candidate.content) as ThemeContent;
      if (normalizeIdentity(parsed.name, parsed.cssVars) === target) {
        return candidate;
      }
    } catch {
      // Malformed stored content — never a reuse match.
    }
  }
  return null;
}
